// Bu dosya, hemşirelik bakım planlarının listelendiği, ekleme, düzenleme ve silme işlemlerinin yapıldığı ana sayfa bileşenidir.
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import {
  RowDataPlan,
  RowDataNursingIntervention,
  RowDataGuide,
} from "./rows";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Box, Card, CardHeader, Checkbox, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import AddGuideModal from "../modals/add-guide-modal";
import { useEffect, useState } from "react";
import ByzMuiDeleteButton from "src/components/core-components/mui/buttons/ByzMuiDeleteButton";
import ByzMuiEditButton from "src/components/core-components/mui/buttons/ByzMuiEditButton";
import AddPlanModal from "../modals/add-plan-modal";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import { GridCellParams } from "@mui/x-data-grid-premium";
import AddNurseInterventionModal from "../modals/add-nurse-intervention-modal";
import ByzNurseAddButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseAddButton";
import { nurseService } from "src/api/nurse/nurseService";
import { useTranslate } from "src/locales";
import { useSnackbar } from "notistack";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import ByzGrdDeleteButton from "src/components/core-components/mui/buttons-grid/ByzGrdDeleteButton";

type DeleteType = "guide" | "plan" | "nursingIntervention";

// Modal durumları
const initialModalState = {
  addGuide: false,
  addPlan: false,
  addNurseIntervention: false,
  editId: 0,
};

// Silme durumu
type DeleteState = {
  open: boolean;
  rowId: string;
  url: string;
  type?: "guide" | "plan" | "nursingIntervention";
};

const initialDeleteState: DeleteState = {
  open: false,
  rowId: "",
  url: "",
  type: undefined,
};

// ByzMuiSnackBar'ın sadece içeriğini döndüren yardımcı fonksiyon
const getSnackBarContent = (title: string, message: string, type: "success" | "error") => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      backgroundColor: type === "success" ? "#E6FAF5" : "#FDEEEE",
      borderRadius: "0.5vh",
      padding: "1rem",
      borderLeft: `1vh solid ${type === "success" ? "#00CC99" : "#EB5757"}`,
    }}
  >
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "50%",
        width: "2.25rem",
        height: "2.25rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img src={type === "success" ? "/assets/icons/toast/success.svg" : "/assets/icons/toast/error.svg"} alt={type} style={{ width: "19px", height: "19px" }} />
    </Box>
    <Box display={"flex"} sx={{ marginLeft: "1rem" }}>
      <Box display={"flex"} flexDirection={"column"} alignItems={"start"} columnGap={"0.5rem"}>
        <ByzMuiTypography
          sx={{ fontWeight: "400", fontSize: "16.5px", display: "flex", marginLeft: "0px" }}
        >
          {title}
        </ByzMuiTypography>
        <ByzMuiTypography
          sx={{ fontWeight: "400", fontSize: "14px", display: "flex", marginLeft: "0px" }}
        >
          {message}
        </ByzMuiTypography>
      </Box>
    </Box>
  </Box>
);

function List() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [modalState, setModalState] = useState(initialModalState);
  const [deleteState, setDeleteState] = useState<DeleteState>(initialDeleteState);
  const [selectedRows, setSelectedRows] = useState<{
    guide: number | null;
    plan: number | null;
    nursingIntervention: number | null;
  }>({
    guide: null,
    plan: null,
    nursingIntervention: null,
  });

  const [rowsGuide, setRowsGuide] = useState<RowDataGuide[]>([]);
  const [isLoadingGuides, setIsLoadingGuides] = useState(true);
  const [rowsPlanDatas, setRowsPlanDatas] = useState<RowDataPlan[]>([]);
  const [rowsNursingInterventionDatas, setRowsNursingInterventionDatas] =
    useState<RowDataNursingIntervention[]>([]);

  const selectedGuideName = selectedRows.guide
    ? rowsGuide.find((g) => g.id === selectedRows.guide)?.name || ""
    : "";
  const selectedPlanName = selectedRows.plan
    ? rowsPlanDatas.find((p) => p.id === selectedRows.plan)?.name || ""
    : "";

  // Silme fonksiyonu
  const handleDeleteRow = async (
    id: string,
    type: "guide" | "plan" | "nursingIntervention"
  ) => {
    const deleteUrls = {
      guide: "/nurse/api/NurseCare/deleteNurseCareGroup/",
      plan: "/nurse/api/NurseCarePlan/delete/",
      nursingIntervention: "/nurse/api/NurseCarePlanDetail/delete/",
    };
    setDeleteState({ open: true, rowId: id, url: deleteUrls[type], type });
  };

  // Silme işlemi sonrası
  const handleDeleteSuccess = (success: boolean) => {
    if (success) {
      const deleteType = deleteState.type;
      if (deleteType === "guide") {
        fetchGuides();
        setRowsPlanDatas([]);
        setRowsNursingInterventionDatas([]);
        setSelectedRows(prev => ({
          ...prev,
          guide: null,
          plan: null,
          nursingIntervention: null
        }));
      } else if (deleteType === "plan") {
        fetchPlans();
        setRowsNursingInterventionDatas([]);
        setSelectedRows(prev => ({
          ...prev,
          plan: null,
          nursingIntervention: null
        }));
      } else if (deleteType === "nursingIntervention") {
        fetchInterventions();
        setSelectedRows(prev => ({
          ...prev,
          nursingIntervention: null
        }));
      }
      setDeleteState(initialDeleteState);
    }
  };

  // Tüm kılavuzları çek
  const fetchGuides = async () => {
    setIsLoadingGuides(true);
    try {
      const res = await nurseService.nurseCare.getAllNurseCareGroups<{ data: any[] }>();
      setRowsGuide(res.data || []);
    } catch (err) {
      console.error("Kılavuzlar alınamadı:", err);
    } finally {
      setIsLoadingGuides(false);
    }
  };

  // Seçili kılavuza göre planları çek
  const fetchPlans = async () => {
    if (!selectedRows.guide) return;
    try {
      const selectedGuide = rowsGuide.find((g) => g.id === selectedRows.guide);
      const ncgid = selectedGuide?.id;
      if (!ncgid) return;
      const res: any = await nurseService.nurseCare.getAllNurseCarePlansByNcgId({ ncgId: ncgid });
      // Alan adlarını dönüştür
      const mapped = (res.data || []).map((plan: any) => ({
        ...plan,
        isactive: plan.isActive // string olarak gelir, aynen aktar
      }));
      setRowsPlanDatas(mapped);
    } catch (err) {
      console.error("Planlar alınamadı:", err);
    }
  };

  // Seçili plana göre müdahaleleri çek
  const fetchInterventions = async () => {
    if (!selectedRows.plan) return;

    //tabsheet tümü aktif olacak ama seçileni gösterecek sadece


    try {
      const res: any = await nurseService.nurseCare.getAllNurseCarePlanDetailsByNcpId({ ncpId: selectedRows.plan! });
      const mapped = (res.data || []).map((intervention: any) => ({
        ...intervention,
        isactive: intervention.isActive,
        period: intervention.period !== undefined && intervention.period !== null ? intervention.period.toString() : ""
      }));

      const convertPeriodTime = mapped.map((x: any) => {
        const hours = Math.floor(parseInt(x.period) / 60);

        return {
          ...x,
          period: `${hours.toString().padStart(2, '0')} ${t("hour")}`
        };
      });

      setRowsNursingInterventionDatas(convertPeriodTime);
    } catch (err) {
      console.error("Müdahaleler alınamadı:", err);
    }
  };

  const handleCheckboxChange = (type: DeleteType, id: number) => {
    setSelectedRows((prev) => {
      const updated = { ...prev };

      if (type === "guide") {
        // Eğer farklı bir guide seçiliyorsa, plan ve intervention sıfırlanmalı
        if (prev.guide !== id) {
          updated.guide = id;
          updated.plan = null;
          updated.nursingIntervention = null;
        } else {
          // Seçili guide tekrar tıklanırsa, kaldır
          updated.guide = null;
          updated.plan = null;
          updated.nursingIntervention = null;
        }
      } else if (type === "plan") {
        if (prev.plan !== id) {
          updated.plan = id;
          updated.nursingIntervention = null;
        } else {
          updated.plan = null;
          updated.nursingIntervention = null;
        }
      } else if (type === "nursingIntervention") {
        updated.nursingIntervention = prev.nursingIntervention !== id ? id : null;
      }

      return updated;
    });
  };

  const renderCheckboxChose = (type: DeleteType, id: number) => (
    <ByzMuiCheckBox
      checked={selectedRows[type] === id}
      onChange={() => handleCheckboxChange(type, id)}
    />
  );

  const renderCheckbox = (checked: boolean) => <ByzMuiCheckBox checked={checked} />;

  const createColumns = (type: any): GridColDef[] => [
    {
      field: "x",
      headerName: t("choose"),
      width: 56,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridCellParams) =>
        renderCheckboxChose(type, params.id as number),
    },
    {
      field: "name",
      headerName:
        type === "guide"
          ? t("nurse.care-plans.guide-columns.guide")
          : type === "plan"
            ? t("nurse.care-plans.plan-columns.nurse-diagnosis")
            : t("nurse.care-plans.nurse-intervention-columns.attempt"),
      flex: 1, // sadece burada flex
      minWidth: 100,
    },
    type === "guide" && {
      field: "note",
      headerName: t("note"),
      minWidth: 120,
      flex: 1,
    },
    type !== "guide" && {
      field: type === "plan" ? "target" : "period",
      headerName:
        type === "plan"
          ? t("nurse.care-plans.plan-columns.target")
          : t("nurse.care-plans.nurse-intervention-columns.period"),
      minWidth: 100,
      flex: 1,
    },
    {
      field: "isActive",
      headerName: t("active"),
      width: 100, // checkbox + padding sığar
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridCellParams) =>
        renderCheckbox(params.row.isactive === "1" || params.row.is_active === "1"),
    },
    {
      field: "edit",
      headerName: t("buttons.edit"),
      width: 150, // Düz + kalem sığar
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <ByzGrdEditButton
            onClick={() => {
              setModalState({
                ...modalState,
                addGuide: type === "guide",
                addPlan: type === "plan",
                addNurseIntervention: type === "nursingInterventions",
                editId: (params.row?.keyid || params.row?.id)?.toString() || "",
              });
            }}
          />
        </Box>
      ),
    },
    {
      field: "delete",
      headerName: t("buttons.delete"),
      width: 150, // Sil + çöp ikon sığar
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <ByzGrdDeleteButton
            onClick={() => {
              let deleteType = type;
              if (type === "nursingInterventions") deleteType = "nursingIntervention";
              // Kılavuz için ncgid gönder
              let rowId = (type === "guide") ? params.row?.id?.toString() : (params.row?.keyid || params.row?.id)?.toString() || "";
              handleDeleteRow(rowId, deleteType);
            }}
          />
        </Box>
      ),
    },
  ].filter(Boolean) as GridColDef[];



  useEffect(() => {
    fetchGuides();
  }, []);

  // Kılavuz değişince sadece planları çek, seçim kaldırılırsa plan ve girişimleri sıfırla
  useEffect(() => {
    if (selectedRows.guide !== null) {
      fetchPlans();
    } else {
      setRowsPlanDatas([]);
      setRowsNursingInterventionDatas([]);
    }
  }, [selectedRows.guide]);

  // Plan değişince sadece hemşirelik girişimlerini çek, seçim kaldırılırsa girişimleri sıfırla
  useEffect(() => {
    if (selectedRows.plan !== null) {
      fetchInterventions();
    } else {
      setRowsNursingInterventionDatas([]);
    }
  }, [selectedRows.plan]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Kartlar */}
      {["guide", "plan", "nursingInterventions"].map((type) => (
        <Card key={type}>
          <CardHeader
            title={
              <>
                {t(
                  type === "nursingInterventions"
                    ? t("nurse.care-plans.nurse-intervention")
                    : `nurse.care-plans.${type}s`
                )}
                {type === "plan" && selectedGuideName && (
                  <span style={{ fontStyle: "italic", marginLeft: 8, fontWeight: 400 }}>
                    {t("nurse.care-plans.guides")}: <span style={{ fontStyle: "italic", marginLeft: 8, fontWeight: 400 }}>{selectedGuideName}</span>
                  </span>
                )}
                {type === "nursingInterventions" && selectedPlanName && (
                  <span style={{ fontStyle: "italic", marginLeft: 8, fontWeight: 400 }}>
                    {t("nurse.care-plans.plans")}: <span style={{ fontStyle: "italic", marginLeft: 8, fontWeight: 400 }}>{selectedPlanName}</span>
                  </span>
                )}
              </>
            }
          />
          <Box sx={{ marginLeft: "20px", paddingTop: "12px" }}>
            <ByzNurseAddButton

              onClick={() => {
                if ((type === "plan" && !selectedRows.guide) ||
                  (type === "nursingInterventions" && (!selectedRows.guide || !selectedRows.plan))) return;
                const modalKey =
                  type === "nursingInterventions"
                    ? "addNurseIntervention"
                    : `add${type.charAt(0).toUpperCase() + type.slice(1)}`;
                setModalState({ ...modalState, [modalKey]: true, editId: 0 });
              }}
            />
            {type === 'guide' && isLoadingGuides ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <ByzMuiDataGrid
                whiteGrid={true}
                columns={createColumns(type)}
                pageSize={10}
                rows={
                  type === "guide"
                    ? rowsGuide
                    : type === "plan"
                      ? rowsPlanDatas
                      : rowsNursingInterventionDatas
                }
                getRowId={(row) => row.keyid ?? row.id}

              />
            )}
          </Box>
        </Card>
      ))}

      {/* Modallar */}
      <OpenComponentDialog
        showPatientData={false}
        title={t("nurse.care-plans.add-guide")}
        open={modalState.addGuide}
        onClose={() => setModalState({ ...modalState, addGuide: false })}
        maxWidth="md"
      >
        <AddGuideModal
          id={modalState.editId}
          onSuccess={() => {
            setModalState({ ...modalState, addGuide: false, editId: 0 });
            fetchGuides();
            enqueueSnackbar(getSnackBarContent(t("add-or-edit-success-title"), t("add-or-edit-success-content"), "success"), { anchorOrigin: { vertical: "top", horizontal: "right" }, content: (key, message) => message });
          }}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        showPatientData={false}
        title={t("nurse.care-plans.add-plan")}
        open={modalState.addPlan}
        onClose={() => setModalState({ ...modalState, addPlan: false })}
        maxWidth="md"
      >
        <AddPlanModal
          selectedGuideId={selectedRows.guide}
          selectedPlanId={Number(modalState.editId)}
          selectedGuideName={selectedGuideName}
          onSuccess={() => {
            setModalState({ ...modalState, addPlan: false, editId: 0 });
            fetchPlans();
            enqueueSnackbar(getSnackBarContent(t("add-or-edit-success-title"), t("add-or-edit-success-content"), "success"), { anchorOrigin: { vertical: "top", horizontal: "right" }, content: (key, message) => message });
          }}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        showPatientData={false}
        title={t("nurse.care-plans.add-nurse-intervention")}
        open={modalState.addNurseIntervention}
        onClose={() => setModalState({ ...modalState, addNurseIntervention: false })}
        maxWidth="md"

      >
        <AddNurseInterventionModal
          selectedGuideId={selectedRows.guide}
          selectedPlanName={selectedPlanName}
          selectedGuideName={selectedGuideName}
          selectedPlanId={selectedRows.plan}
          selectedInterventionId={Number(modalState.editId)}
          onSuccess={() => {
            setModalState({ ...modalState, addNurseIntervention: false, editId: 0 });
            fetchInterventions();
            enqueueSnackbar(getSnackBarContent(t("add-or-edit-success-title"), t("add-or-edit-success-content"), "success"), { anchorOrigin: { vertical: "top", horizontal: "right" }, content: (key, message) => message });
          }}
        />
      </OpenComponentDialog>

      <ByzDeleteConfirmDialog
        open={deleteState.open}
        onClose={() => setDeleteState({ ...deleteState, open: false })}
        patientId={deleteState.rowId}
        url={deleteState.url}
        onSuccess={handleDeleteSuccess}
        title={t("definition-delete")}
        content={t("definition-delete-content")}
      />
    </div>
  );
}

export default List;
